<template>
  <section class="form">
    <h3 class="form__heading form__error-message-headline">{{ title }}</h3>
    <div class="form__group form__group--message form__group--center">
      <div class="form__subheading">
        <div v-if="message || report" class="form__error-message">
          <span
            class="form__error-message--copy"
            :title="report ? 'Copy error report' : 'Copy message'"
            @click="copyReport"
            v-html="iconCopy"
          />
          <span v-if="copied" class="form__error-message--copied">Copied</span>
          <code>
            {{ message || 'No further details available' }}
          </code>
        </div>
        <details v-if="stackTrace" class="form__stack-trace">
          <summary>Stack trace</summary>
          <pre>{{ stackTrace }}</pre>
        </details>
        <p class="form__error-message-info">
          If the problem persists please contact Resourced support<template v-if="report">
            and give them the error report above</template
          >. Press below to reload browser and continue.
        </p>
        <div>
          <template v-if="mode === 'refresh' || redirectCancelled">
            <fs-button @click="reload"
              ><span class="form__button--icon" v-html="refreshCircleArrows" />Reload</fs-button
            >
          </template>
          <template v-else>
            You will automatically be redirected to the start page in <strong>{{ countDownInSeconds }}</strong>
          </template>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import iconCopy from '@icons/interface/copy-outline.svg';
import refreshCircleArrows from '@icons/interface/refresh-circle-arrows.svg';
import FsButton from '@/components/buttons/fs-button.vue';

export interface ErrorProps {
  title?: string;
  message?: string;
  /** Full technical report (timestamp, user, request, correlation id, ...) to copy. */
  report?: string;
  stackTrace?: string;
  mode?: 'refresh' | 'redirect';
}

const { mode = 'refresh', message, report } = defineProps<ErrorProps>();

const emit = defineEmits<{ close: [] }>();

const router = useRouter();

const countDownInSeconds = ref(10);
const redirectCancelled = ref(false);
let interval: ReturnType<typeof setInterval> | null = null;
let timeout: ReturnType<typeof setTimeout> | null = null;

const copied = ref(false);

const copyReport = async () => {
  // Copying is the whole point of this screen — don't navigate away mid-copy.
  cancelRedirect();

  try {
    await navigator.clipboard.writeText(report || message || '');
    copied.value = true;
  } catch (err) {
    console.error('Failed to copy text: ', err);
  }
};

const cancelRedirect = () => {
  if (interval) clearInterval(interval);
  if (timeout) clearTimeout(timeout);
  interval = null;
  timeout = null;
  redirectCancelled.value = true;
};

const reload = () => {
  window.location.reload();
};

onMounted(() => {
  interval = setInterval(() => {
    countDownInSeconds.value--;
  }, 1000);

  timeout = setTimeout(() => {
    if (interval) clearInterval(interval);

    if (mode === 'redirect') {
      emit('close');
      router.push({ name: 'products' });
    }
  }, countDownInSeconds.value * 1000);
});

onBeforeUnmount(() => {
  if (interval) clearInterval(interval);
  if (timeout) clearTimeout(timeout);
});
</script>

<style lang="scss" scoped>
@import '@styles/variables';
.form {
  &__heading {
    font-size: 3rem;
    text-wrap: balance;
    margin: 2rem 0 3rem 0;
  }
  &__button {
    &--icon {
      width: 1.6rem;
      height: 1.6rem;
      margin-right: 0.8rem;
      vertical-align: middle;
    }
  }
  &__stack-trace {
    margin: 0 2rem;
    text-align: left;
    font-style: normal;

    summary {
      cursor: pointer;
      font-size: 1.2rem;
    }

    pre {
      max-height: 40vh;
      overflow: auto;
      margin-top: 1rem;
      padding: 1rem;
      border-radius: 1rem;
      background: $c-light-gray;
      font-size: 1.1rem;
      white-space: pre;
    }
  }
  &__error-message-info {
    font-size: 1.4rem;
    margin: 2rem 0;
    text-wrap: balance;
    font-style: normal;
  }

  &__error-message {
    position: relative;
    font-family: 'monospace', monospace;
    font-style: normal;
    letter-spacing: -0.5px;
    font-size: 1.2rem;
    background: $c-light-gray;
    padding: 3rem 1rem 3rem 1rem;
    border-radius: 1rem;
    margin: 2rem;
    text-wrap: balance;

    &--copied {
      position: absolute;
      top: 1.2rem;
      right: 3.6rem;
      font-size: 1.1rem;
      color: $c-gray;
    }

    &--copy {
      z-index: 9;
      position: absolute;
      top: 1rem;
      right: 1rem;
      width: 2rem;
      height: 2rem;
      padding: 0.4rem;
      border-radius: 0.4rem;
      transition: all $transition-fast ease-in-out;
      &:hover {
        cursor: pointer;
        background: $c-gray-mid-light;
      }
    }
  }
}
</style>
